import { useForm } from 'react-hook-form'

import { useApolloClient } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Dialog, F0Select } from '@factorialco/f0-react'
import { Pin } from '@factorialco/f0-react/icons/app'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  GenerateAutoScheduleDocument,
  ShiftManagementAutoScheduleStatusDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import { Form } from 'modules/shift_management/lib/forms/AutoSchedule/form'
import { AutoScheduleSchemaType } from 'modules/shift_management/lib/forms/AutoSchedule/schema'
import { useSelectableLocations } from 'modules/shift_management/lib/useSelectableLocations'

import { Rules } from './Rules'

interface Props {
  onClose: () => void
  setIsSubscribed: (isSubscribed: boolean) => void
}

const AutoScheduleModal = observer(({ onClose, setIsSubscribed }: Props) => {
  const [{ locationIds }] = useFilters()
  const { employee } = useCurrentSession()
  const employeeId = employee?.id

  const apolloClient = useApolloClient()
  const [{ canCreateShiftsForEverybody }] = usePermissions()
  const [{ view }] = useSchedule()

  const { defaultValues, schema } = Form()
  const form = useForm<AutoScheduleSchemaType>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues,
  })
  const { watch, setValue } = form
  const locationId = watch('locationId')
  const employeeSelection = watch('employeeSelection')

  const { loading: locationsLoading, locations } = useSelectableLocations(
    employeeId,
    canCreateShiftsForEverybody
  )
  const currentLocationId = useUnsafeMemo(() => {
    return locationId ?? locationIds?.at(0) ?? ''
  }, [locationIds, locationId])

  const { selectableEmployeeIds, loading: loadingEmployees } = useSelectableEmployees(
    view.startAt,
    view.endAt,
    'create',
    [currentLocationId]
  )

  useUnsafeEffect(() => {
    if (loadingEmployees || !selectableEmployeeIds.length) return

    setValue('employeeSelection', { type: 'manual', ids: selectableEmployeeIds })
  }, [selectableEmployeeIds.length, loadingEmployees, setValue])

  useUnsafeEffect(() => {
    if (!view.startAt || !view.endAt) return

    setValue('startAt', view.startAt)
    setValue('endAt', view.endAt)
    setValue('locationId', currentLocationId)
  }, [view.startAt, view.endAt])

  const [createAutoSchedule, { loading }] = useMutation(GenerateAutoScheduleDocument, {
    extractorFn: (data) => data.shiftManagementMutations.generateScheduleShiftmanagementAutomation,
  })

  const onSubmit = async (closePortal: ClosePortalFn) => {
    track(trackingEvents.shiftManagement.autoSchedulerRun, {
      from: 'coverage_section',
      location_id: currentLocationId,
      agreement_hours: form.getValues('agreementHours'),
      max_work_hours_per_day: form.getValues('maxWorkHoursPerDay'),
      rest_days_per_week: form.getValues('restDaysPerWeek'),
      rest_hours_between_shifts: form.getValues('restHoursBetweenShifts'),
      start_date: form.getValues('startAt'),
      end_date: form.getValues('endAt'),
      employees_count:
        employeeSelection?.type === 'manual'
          ? employeeSelection.ids?.length
          : selectableEmployeeIds.length,
    })

    const res = await createAutoSchedule({
      variables: {
        agreementHours: form.getValues('agreementHours'),
        employeeSelection,
        endAt: form.getValues('endAt'),
        locationId: currentLocationId,
        maxWorkHoursPerDay: form.getValues('maxWorkHoursPerDay'),
        restDaysPerWeek: form.getValues('restDaysPerWeek'),
        restHoursBetweenShifts: form.getValues('restHoursBetweenShifts'),
        startAt: form.getValues('startAt'),
      },
    })
    apolloClient.refetchQueries({
      include: [ShiftManagementAutoScheduleStatusDocument],
    })
    const handled = handleResult(res, (_errors) => {})
    if (handled.success) {
      setIsSubscribed(true)
      closePortal()
    }
  }

  return (
    <F0Dialog
      isOpen
      width="md"
      onClose={() => {
        if (loading) return
        onClose()
      }}
      title={i18n.t('shift_management.coverage_section.automation.modal.title')}
      description={i18n.t('shift_management.coverage_section.automation.modal.description')}
      primaryAction={{
        label: i18n.t('shift_management.coverage_section.automation.modal.submit'),
        onClick: () => onSubmit(onClose),
        disabled:
          loading ||
          (employeeSelection.type === 'manual' && employeeSelection.ids?.length === 0) ||
          !currentLocationId,
        loading: loading,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <Box width="full" flexDirection="column" gap="s16">
        <F0Select
          options={locations.map((location) => ({
            label: location.name,
            value: location.id.toString(),
          }))}
          value={currentLocationId}
          onChange={(value: string) => {
            if (value === currentLocationId) return
            setValue('locationId', value)
            setValue('employeeSelection', { type: 'manual', ids: [] })
          }}
          size="md"
          loading={locationsLoading}
          icon={Pin}
          label={i18n.t('shift_management.coverage_section.generate_coverage_modal.workplace')}
          showSearchBox
        />

        <Rules form={form} />

        <F0Alert
          description={i18n.t(
            'shift_management.coverage_section.automation.modal.info.description'
          )}
          title={i18n.t('shift_management.coverage_section.automation.modal.info.title')}
          variant="info"
          link={{
            href: 'https://help.factorialhr.com/en_US/shift-management/automatic-shift-planning',
            label: i18n.t('common.learn_more'),
          }}
        />

        {loadingEmployees ? (
          <Loading />
        ) : (
          <EmployeeSelectorV2
            key={`employee-selector-${currentLocationId}`}
            mandatory
            messages={{
              placeholder: i18n.t('common.employees'),
            }}
            forcedFilters={{
              employeeIds: selectableEmployeeIds,
            }}
            filters={['!workplace']}
            value={employeeSelection?.type === 'manual' ? employeeSelection.ids : undefined}
            onChange={(value) => {
              setValue('employeeSelection', value)
            }}
            multiple
            options={{
              hideSearchIcon: true,
              asList: false,
            }}
          />
        )}
      </Box>
    </F0Dialog>
  )
})

export { AutoScheduleModal }
